/**
 * @file "Open on patient's phone" — the QR / share-link dialog.
 * @module pages/healthcare/formsInTray/FormShareLinkModal
 */

import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Divider, Modal, Space, Spin, Tooltip, Typography } from "antd";
import {
	ExclamationCircleOutlined, CopyOutlined, MailOutlined, MessageOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";

import { ENV } from "../../../config/runtimeEnv";
import { AssignedForm, issueFormLink } from "../../../api/formApi";
import { buildPatientFormLink } from "../../../utils/forms/patientFormLink";
import { buildFormAssignedNotification, FormLinkChannel } from "../../../utils/forms/formAssignedNotification";
import { sendCommunication } from "../../../api/communications/communicationApi";
import { useNotifications } from "../../../context/NotificationContext";
import { Patient } from "../../../types/healthcare/PatientTypes";

const { Paragraph, Text } = Typography;

/**
 * 24 hours, stated by the REQUEST rather than inherited from the backend.
 *
 * `link-issued` deliberately has no default expiry, so the emailed path (which wants a link a
 * patient may open days later) and this one can differ. A caller that forgets to ask gets the
 * safe-to-email behaviour; a caller that wants a fuse has to say so. This is that caller.
 */
export const QR_LINK_EXPIRY_MINUTES = 24 * 60;

/** ≥256px, plus a 4-module quiet zone — the spec's minimum, and what makes it scan from across a desk. */
const QR_SIZE_PX = 256;

export interface FormShareLinkModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** The patient-typed assignment whose link is being shared. */
	assignedForm: AssignedForm;
	/**
	 * The patient, for the notification payload and the on-screen copy. Separate from
	 * `assignedForm.patient` because the Forms tab holds the live patient record while the
	 * tray row holds a snapshot — the caller knows which is better.
	 */
	patient?: Partial<Patient> | null;
	/** Clinic company id for `sendNotificationWithCompanyConfig`. */
	companyId?: number;
}

const FormShareLinkModal: React.FC<FormShareLinkModalProps> = ({
	isOpen,
	onClose,
	assignedForm,
	patient,
	companyId,
}) => {
	const [link, setLink] = useState<string | null>(null);
	const [expiresAt, setExpiresAt] = useState<string | null>(null);
	const [isIssuing, setIsIssuing] = useState(false);
	const [issueFailed, setIssueFailed] = useState(false);
	const [sendingChannel, setSendingChannel] = useState<FormLinkChannel | null>(null);
	const { showSuccess, showError } = useNotifications();

	const notificationsEnabled = ENV.VITE_NOTIFICATIONS_ENABLED === "true";
	const patientName = patient
		? `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim()
		: "";

	/**
	 * `rotate` is the ONLY difference between showing the link and replacing it — same act,
	 * same audit event, one boolean apart. See `issueFormLink`'s docblock.
	 */
	const issue = useCallback(
		async (rotate: boolean) => {
			setIsIssuing(true);
			setIssueFailed(false);
			try {
				const issued = await issueFormLink(assignedForm.id, {
					rotate,
					expiresInMinutes: QR_LINK_EXPIRY_MINUTES,
				});
				setLink(buildPatientFormLink(issued.assigned_form_id, issued.access_token));
				setExpiresAt(issued.access_token_expires_at);
				return true;
			} catch {
				// Nothing partial is left on screen: a stale QR beside a failed refresh is a link
				// the practitioner would show to a patient believing it had just been reissued.
				setLink(null);
				setExpiresAt(null);
				setIssueFailed(true);
				return false;
			} finally {
				setIsIssuing(false);
			}
		},
		[assignedForm.id],
	);

	useEffect(() => {
		if (!isOpen) return;
		setLink(null);
		setExpiresAt(null);
		setIssueFailed(false);
		issue(false);
	}, [isOpen, issue]);

	/**
	 * ═══ EXPOSURE HYGIENE ════════════════════════════════════════════════════════════════
	 * A QR on a screen is readable by anyone in the room, and this one opens a named
	 * patient's questionnaire. In a shared consulting room or a waiting area, a dialog left
	 * sitting open while the practitioner is elsewhere is real exposure — so it closes as
	 * soon as this window stops being the thing in front of them.
	 *
	 * `blur` covers alt-tabbing and switching to another app; `visibilitychange` covers the
	 * tab being backgrounded or the screen being locked; `popstate` covers navigating away
	 * within the SPA. Closing is lossless — reopening reissues, and `rotate: false` returns
	 * the same link.
	 */
	useEffect(() => {
		if (!isOpen) return;

		const closeIfHidden = () => { if (document.visibilityState === "hidden") onClose(); };
		window.addEventListener("blur", onClose);
		window.addEventListener("popstate", onClose);
		document.addEventListener("visibilitychange", closeIfHidden);
		return () => {
			window.removeEventListener("blur", onClose);
			window.removeEventListener("popstate", onClose);
			document.removeEventListener("visibilitychange", closeIfHidden);
		};
	}, [isOpen, onClose]);

	const handleCopy = async () => {
		if (!link) return;
		try {
			await navigator.clipboard.writeText(link);
			showSuccess("Link copied");
		} catch {
			showError("Could not copy the link. Select it and copy manually.");
		}
	};

	const handleSend = async (channel: Exclude<FormLinkChannel, "both">) => {
		if (!link) return;
		setSendingChannel(channel);
		try {
			// The token that is actually on screen, not a re-fetch: the patient must receive the
			// same link the QR encodes, or "I already scanned it" and "I got your text" become
			// two different half-finished forms.
			const token = new URL(link, window.location.origin).searchParams.get("t");

			await sendCommunication(
				buildFormAssignedNotification({
					assignedFormId: assignedForm.id,
					accessToken: token,
					patient,
					companyId: companyId ?? 0,
					channel,
				}),
			);
			showSuccess(channel === "email" ? "Link emailed to the patient" : "Link sent by SMS");
		} catch {
			showError(channel === "email" ? "Failed to email the link" : "Failed to send the SMS");
		} finally {
			setSendingChannel(null);
		}
	};

	/**
	 * Confirmed, because it is destructive in a way nothing on screen shows: the link in the
	 * patient's inbox stops working the moment this returns, and there is no undo.
	 */
	const handleRotate = () => {
		Modal.confirm({
			title: "Generate a new link?",
			icon: <ExclamationCircleOutlined />,
			content:
				"The current QR code and any link already sent to this patient will stop working "
				+ "immediately. Use this if the code was shown to the wrong person or sent to the "
				+ "wrong number.",
			okText: "Generate new link",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				if (await issue(true)) showSuccess("New link generated — the previous one no longer works");
				else showError("Could not generate a new link. The previous one still works.");
			},
		});
	};

	const expiryLine = expiresAt
		? (() => {
			try {
				return `This link works until ${format(new Date(expiresAt), "d MMM yyyy, h:mm a")}.`;
			} catch {
				return "This link expires in 24 hours.";
			}
		})()
		: "This link does not expire.";

	const busy = isIssuing || sendingChannel !== null;

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			width={480}
			destroyOnClose
			title="Open on patient's phone"
			footer={<Button onClick={onClose}>Close</Button>}
		>
			<div data-testid="form-share-link-modal" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<Paragraph style={{ marginBottom: 0 }}>
					{patientName
						? `${patientName} can scan this with their phone camera to fill the form on their own device.`
						: "The patient can scan this with their phone camera to fill the form on their own device."}{" "}
					Nothing from your clinical session goes to their phone — the link opens the form and
					nothing else.
				</Paragraph>

				{isIssuing ? (
					<div style={{ textAlign: "center", padding: 48 }}>
						<Spin />
					</div>
				) : issueFailed ? (
					<Alert
						type="error"
						showIcon
						data-testid="form-share-link-error"
						message="Could not prepare the link"
						description="The form may have been removed, or it may not be a patient form. Try again."
						action={<Button size="small" onClick={() => issue(false)}>Retry</Button>}
					/>
				) : link ? (
					<>
						{/*
							White plate behind the code regardless of theme, plus a 4-module quiet zone.
							A QR rendered on a dark surface, or flush to its container edge, is the single
							most common reason a phone camera refuses to lock on.
						*/}
						<div
							style={{
								alignSelf: "center",
								background: "#ffffff",
								padding: 16,
								borderRadius: 8,
								lineHeight: 0,
							}}
						>
							<QRCodeSVG
								data-testid="form-share-qr"
								value={link}
								size={QR_SIZE_PX}
								level="M"
								marginSize={4}
								bgColor="#ffffff"
								fgColor="#000000"
								title="Patient form link"
							/>
						</div>

						{/*
							The full URL, selectable. This is the ONLY rendering of the token anywhere in
							this component, and it is inseparable from the link it belongs to.
						*/}
						<Text
							data-testid="form-share-link-url"
							code
							style={{ userSelect: "text", wordBreak: "break-all", fontSize: 11 }}
						>
							{link}
						</Text>

						<Space wrap>
							<Button icon={<CopyOutlined />} data-testid="form-share-copy" onClick={handleCopy}>
								Copy link
							</Button>
							<Tooltip title={
								!notificationsEnabled
									? "Notifications are switched off for this environment."
									: !patient?.email ? "This patient has no email address on file." : undefined
							}>
								<Button
									icon={<MailOutlined />}
									data-testid="form-share-send-email"
									loading={sendingChannel === "email"}
									disabled={busy || !notificationsEnabled || !patient?.email}
									onClick={() => handleSend("email")}
								>
									Send by email
								</Button>
							</Tooltip>
							<Tooltip title={
								!notificationsEnabled
									? "Notifications are switched off for this environment."
									: !patient?.phone ? "This patient has no phone number on file." : undefined
							}>
								<Button
									icon={<MessageOutlined />}
									data-testid="form-share-send-sms"
									loading={sendingChannel === "sms"}
									disabled={busy || !notificationsEnabled || !patient?.phone}
									onClick={() => handleSend("sms")}
								>
									Send by SMS
								</Button>
							</Tooltip>
						</Space>

						{/*
							⚠️ The second sentence is not padding. There is one token per assignment, so
							asking for a 24h window here shortens every copy of this link — including one
							already emailed. See this file's header for why that is still the right
							trade-off, and why saying so is the part that makes it acceptable.
						*/}
						<Text type="secondary" data-testid="form-share-expiry" style={{ fontSize: 12 }}>
							{expiryLine}{" "}
							{expiresAt
								? "This applies to every copy of the link for this form, including one already emailed to the patient."
								: ""}
						</Text>

						<Divider style={{ margin: "4px 0" }} />

						<div>
							<Button
								type="text"
								danger
								size="small"
								icon={<ReloadOutlined />}
								data-testid="form-share-rotate"
								disabled={busy}
								onClick={handleRotate}
							>
								Generate a new link
							</Button>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Replaces this code and stops the current link working — including one already
								sent to the patient.
							</Text>
						</div>
					</>
				) : null}
			</div>
		</Modal>
	);
};

export default FormShareLinkModal;
